/**
 * The breadcrumb trail, published by whichever screen is on show.
 *
 * The shell used to derive this by splitting the URL, which produced
 * `Home / teach / courses / 019f9efe-a039-71a0-a4b2-0075a651acb1` — three raw
 * path segments and a UUID, in English, in an Arabic interface. A path segment
 * is not a label: `teach` is a route, "Teach" is a translated word, and an id is
 * not a word at all.
 *
 * So the screen declares its own trail. It is the only thing that knows the
 * course is called "إدارة الممثلين" rather than `019f9efe…`, and it already has
 * the data loaded. `useMatches` with route `handle` would be the idiomatic React
 * Router answer, but each portal mounts its own nested `<Routes>` inside a splat
 * route, so the root router cannot see those matches.
 *
 * Split from the provider so neither module exports both a component and a hook
 * — Fast Refresh cannot hot-swap a module that does.
 */
import { createContext, use } from 'react'

export type Crumb = {
  /** Already translated. Nothing here goes through `t()`. */
  label: string
  /** Omit for the current page, which is not a link. */
  to?: string
}

export type BreadcrumbApi = {
  trail: Crumb[]
  setTrail: (trail: Crumb[]) => void
}

export const BreadcrumbContext = createContext<BreadcrumbApi | null>(null)

/**
 * Read the trail. Returns an empty one outside a provider rather than throwing:
 * a screen rendered in isolation by a test is not a bug.
 */
export function useBreadcrumbApi(): BreadcrumbApi {
  return use(BreadcrumbContext) ?? { trail: [], setTrail: () => {} }
}
